import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {useLocation, useMatch} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {useIsMobile} from '../../shared/responsive'

const GisContext = createContext()

export const GisMobileView = {
  overview: 'overview',
  fieldList: 'field-list',
  fieldDetail: 'field-detail',
  landDetail: 'land-detail',
  updateField: 'update-field',
  updateLand: 'update-land',
  createField: 'create-field',
  createLand: 'create-land',
  createGap: 'create-gap',
  reports: 'reports',
  reportsNdvi: 'reports-ndvi',
  reportsTechnical: 'reports-technical',
}

export const GisMobilePanelMode = {
  collapsed: 'collapsed',
  expanded: 'expanded',
}

const INLINE_DRAW_VIEWS = [
  GisMobileView.createField,
  GisMobileView.createLand,
  GisMobileView.createGap,
]

const getDrawerTitle = (activeView, t) => {
  switch (activeView) {
    case GisMobileView.fieldList:
      return t('fields:list.title')
    case GisMobileView.fieldDetail:
      return t('fields:labels.field')
    case GisMobileView.landDetail:
      return t('fields:labels.land')
    case GisMobileView.updateField:
      return t('fields:update.title')
    case GisMobileView.updateLand:
      return t('lands:update.title')
    case GisMobileView.createField:
      return t('fields:buttons.create_field')
    case GisMobileView.createLand:
      return t('fields:buttons.add_lands')
    case GisMobileView.createGap:
      return t('fields:buttons.add_no_agro_zone')
    case GisMobileView.reports:
      return t('reports:selector.title')
    case GisMobileView.reportsNdvi:
      return t('reports:selector.ndvi_report')
    case GisMobileView.reportsTechnical:
      return t('reports:selector.technical_report')
    case GisMobileView.overview:
    default:
      return 'MENU'
  }
}

const getDefaultPanelMode = (activeView, keepOverviewOpen = false) => {
  if (activeView === GisMobileView.overview) {
    return keepOverviewOpen ? GisMobilePanelMode.expanded : GisMobilePanelMode.collapsed
  }

  return GisMobilePanelMode.expanded
}

export function useGisContext() {
  return useContext(GisContext)
}

export const GisConsumer = GisContext.Consumer

export function GisProvider({children}) {
  const {t} = useTranslation()
  const isMobile = useIsMobile()
  const location = useLocation()
  const isFieldList = useMatch('/gis/field/list')
  const isReports = useMatch('/gis/reports/*')
  const isReportsNdvi = useMatch('/gis/reports/ndvi/*')
  const isReportsTechnical = useMatch('/gis/reports/technical/*')
  const isCreateField = useMatch('/gis/field/new')
  const isCreateLand = useMatch('/gis/field/view/:field_id/land/new')
  const isCreateGap = useMatch('/gis/field/view/:field_id/gap/new')
  const isUpdateLand = useMatch('/gis/field/view/:field_id/land/view/:land_id/update')
  const isLandDetail = useMatch('/gis/field/view/:field_id/land/view/:land_id/*')
  const isUpdateField = useMatch('/gis/field/view/:field_id/update')
  const isFieldDetail = useMatch('/gis/field/view/:field_id/*')

  const activeView = useMemo(() => {
    if (isCreateField) {
      return GisMobileView.createField
    }

    if (isCreateLand) {
      return GisMobileView.createLand
    }

    if (isCreateGap) {
      return GisMobileView.createGap
    }

    if (isUpdateLand) {
      return GisMobileView.updateLand
    }

    if (isLandDetail) {
      return GisMobileView.landDetail
    }

    if (isUpdateField) {
      return GisMobileView.updateField
    }

    if (isFieldDetail) {
      return GisMobileView.fieldDetail
    }

    if (isFieldList) {
      return GisMobileView.fieldList
    }

    if (isReportsNdvi) {
      return GisMobileView.reportsNdvi
    }

    if (isReportsTechnical) {
      return GisMobileView.reportsTechnical
    }

    if (isReports) {
      return GisMobileView.reports
    }

    return GisMobileView.overview
  }, [
    isCreateField,
    isCreateGap,
    isCreateLand,
    isUpdateField,
    isFieldDetail,
    isFieldList,
    isUpdateLand,
    isLandDetail,
    isReportsNdvi,
    isReportsTechnical,
    isReports,
  ])

  const keepOverviewOpen =
    activeView === GisMobileView.overview && Boolean(location.state?.keepDrawerOpen)

  const [panelMode, setPanelMode] = useState(() =>
    getDefaultPanelMode(activeView, keepOverviewOpen)
  )

  useEffect(() => {
    if (!isMobile) {
      setPanelMode(GisMobilePanelMode.expanded)
      return
    }

    setPanelMode(getDefaultPanelMode(activeView, keepOverviewOpen))
  }, [activeView, isMobile, keepOverviewOpen, location.key])

  const openPanel = useCallback(() => {
    setPanelMode(GisMobilePanelMode.expanded)
  }, [])

  const closePanel = useCallback(() => {
    setPanelMode(GisMobilePanelMode.collapsed)
  }, [])

  const togglePanel = useCallback(() => {
    setPanelMode((currentMode) =>
      currentMode === GisMobilePanelMode.expanded
        ? GisMobilePanelMode.collapsed
        : GisMobilePanelMode.expanded
    )
  }, [])

  useEffect(() => {
    if (!isMobile) {
      return undefined
    }

    const onDocumentClick = (event) => {
      if (!(event.target instanceof Element)) {
        return
      }

      const toggleElement = event.target.closest('[data-gis-mobile-panel-toggle="true"]')
      if (!toggleElement) {
        return
      }

      togglePanel()
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePanel()
      }
    }

    document.addEventListener('click', onDocumentClick)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('click', onDocumentClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [closePanel, isMobile, togglePanel])

  const value = useMemo(
    () => ({
      activeView,
      closePanel,
      drawerTitle: getDrawerTitle(activeView, t),
      isMobile,
      isOverviewView: activeView === GisMobileView.overview,
      isPanelExpanded: panelMode === GisMobilePanelMode.expanded,
      prefersInlineDrawUI: INLINE_DRAW_VIEWS.includes(activeView),
      openPanel,
      panelMode,
      shouldShowDrawTools: [
        GisMobileView.createField,
        GisMobileView.createLand,
        GisMobileView.createGap,
      ].includes(activeView),
      shouldShowRouteContent: activeView !== GisMobileView.overview,
      togglePanel,
    }),
    [activeView, closePanel, isMobile, openPanel, panelMode, t, togglePanel]
  )

  return <GisContext.Provider value={value}>{children}</GisContext.Provider>
}
